import React from 'react';
import { withTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Form,
  FormInstance,
  InputGroup,
  Schema,
  toaster,
} from 'rsuite';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { constant, util } from '@helper';
import _ from 'lodash';

import { AuthAPI, UserAPI } from '@services/api';
import AuthStorage from '@common/services/authStorage';

import { LoginPayload } from '@common/ApiTypes';
import constants from '@helper/constants';

import linkedinLogo from '@assets/logo-linkedin-white.png';
import googleLogo from '@assets/logo-google.png';

const { withRouter } = util;
const { StringType } = Schema.Types;

// The page paints its own full-bleed background. `m-auto` on the content
// wrapper keeps it centered while still collapsing to the padding when the
// form is taller than the viewport, so it never gets clipped at the top.
const SCREEN_CLASS = [
  'flex min-h-screen flex-col px-4 pt-[max(6rem,12.5vh)]',
  'pb-[max(1.5rem,3.5vh)] font-semibold leading-[1.4] text-[#212121]',
].join(' ');

// The background is a fixed viewport layer (not a class on the scroll
// container) so it still covers the visible area when the document is
// taller than this screen — e.g. phones, where the document gains extra
// height and would otherwise show the white body background at the bottom.
// It must NOT use a negative z-index: `.app`/`body` have white backgrounds,
// and a negative z-index layer would paint underneath them. Instead the
// panel is given its own stacking context (relative z-0) so it paints
// above the layer, which comes first in DOM order.
const BACKGROUND_CLASS = [
  'fixed inset-0 bg-candidate-login bg-cover bg-no-repeat',
].join(' ');

const PANEL_CLASS = 'relative z-0 m-auto w-full max-w-xl px-5';

type State = {
  login: LoginPayload;
  forwardLocation?: string;

  showPassword: boolean;
  rememberMe: boolean;
  verifyEmail: string;
};

class CandidateLoginScreen extends React.Component<any, State> {
  formSignInRef: React.Ref<
    FormInstance<
      Record<string, any>,
      string,
      {
        [x: string]: string | undefined;
      }
    >
  >;

  formLoginSchemaModel: any;

  constructor(props: any) {
    super(props);

    this.state = {
      login: {
        email: '',
        password: '',
      },
      verifyEmail: '',
      showPassword: false,
      rememberMe: false,
    };

    this.formSignInRef = React.createRef();
    this.formLoginSchemaModel = Schema.Model({
      email: StringType()
        .isEmail('Invalid email address format')
        .isRequired('Please fill the required field'),
      password: StringType().isRequired('Please fill the required field'),
    });
  }

  async componentDidMount(): Promise<void> {
    const { location, navigate } = this.props;
    const search = _.get(location, 'search');
    const forward = _.get(location, 'state.from') || '';
    const query = new URLSearchParams(search);
    const bearerToken = query.get('bearer_token');
    const forwardAuth = query.get('forward');

    if (forward) {
      this.setState({ forwardLocation: forward });
    }

    if (bearerToken) {
      const storage = AuthStorage.getInstance();
      storage.setIsAuthenticated(true);
      storage.setAccessToken(`Bearer ${bearerToken}`);
      const res: object = await UserAPI.getProfile();
      const u: any = _.get(res, 'data');
      storage.setUser(u);

      let loc = '/dashboard/candidate/home';
      if (forwardAuth) {
        loc = forwardAuth;
      }
      navigate(loc, { replace: true });
      window.location.reload();
    }
  }

  async componentDidUpdate(): Promise<void> {
    const { location } = this.props;
    const search = _.get(location, 'search');
    const query = new URLSearchParams(search);

    const token = query.get('token');
    const email = query.get('email');

    const { verifyEmail: currEmail } = this.state;

    if (email && token) {
      if (email !== currEmail) {
        this.verifyEmail(email, token);
      }
    }
  }

  private verifyEmail = async (email: string, token: string): Promise<void> => {
    const { t } = this.props;

    try {
      await AuthAPI.verifyEmail(email, token);

      toaster.push(
        util.buildMessage(
          t(constant.translation.profile.formSuccessVerifyEmailKey),
        ),
      );
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    } finally {
      this.setState({ verifyEmail: email });
    }
  };

  private onFormValueChanged = (value: object, key: string): void =>
    this.setState((prevState) => ({
      ...prevState,
      [key]: value,
    }));

  private onGoogleSignInButtonClicked = (): void => {
    try {
      const { forwardLocation } = this.state;

      window.open(
        // eslint-disable-next-line max-len
        `${process.env.REACT_APP_BASE_URL}/api/v1/auth/google?forward=${forwardLocation}`,
        '_self',
      );
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private onLinkedInSignInButtonClicked = (): void => {
    try {
      const { forwardLocation } = this.state;
      window.open(
        // eslint-disable-next-line max-len
        `${process.env.REACT_APP_BASE_URL}/api/v1/auth/linkedin?forward=${forwardLocation}`,
        '_self',
      );
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));
    }
  };

  private onSignInButtonClicked = async (): Promise<void> => {
    const { t, navigate } = this.props;

    if (!_.invoke(this.formSignInRef, 'current.check')) {
      toaster.push(
        util.buildErrorMessage(
          t(constant.translation.login.formErrorValidationMessageKey),
        ),
      );
      return;
    }

    try {
      const { login: payload, forwardLocation } = this.state;
      const res = await AuthAPI.login(payload);
      const data: any = _.get(res, 'data');

      toaster.push(
        util.buildMessage(
          t(constant.translation.login.formSuccessValidationMessageKey),
        ),
      );

      // Set access token
      const { user } = data;
      const authStorage = AuthStorage.getInstance();
      authStorage.setUser(user);

      const profile = _.get(user, 'profile');
      const appRole = _.get(user, 'appRole');

      if (appRole === 'USER') {
        if (_.isEmpty(profile)) {
          navigate('/basic-info', { replace: true });
        }
      }

      let nextLocation = '/';
      if (forwardLocation) {
        nextLocation = forwardLocation;
      }
      navigate(nextLocation, { replace: true });
      window.location.reload();
    } catch (e: any) {
      // Throw pop-up error
      const response = _.get(e, 'response.data');
      const errors: string | Array<any> | any =
        _.get(response, 'errors') || _.get(response, 'message');
      let message;

      if (_.isArray(errors)) {
        message = errors.join('\n');
      } else {
        message = errors;
      }

      toaster.push(util.buildErrorMessage(message));

      // Erase the password
      this.setState((prevState) => ({
        ...prevState,
        login: {
          ...prevState.login,
          password: '',
        },
      }));
    }
  };

  render({ t } = this.props): JSX.Element | null {
    const { showPassword, rememberMe } = this.state;

    return (
      <div className={SCREEN_CLASS}>
        <div aria-hidden className={BACKGROUND_CLASS} />
        <div className={PANEL_CLASS}>
          <header className="px-5 pb-5">
            <p
              // eslint-disable-next-line max-len
              className="mb-4 !leading-[1.2] text-center text-[32px] font-bold whitespace-pre-line"
            >
              {t(constant.translation.login.pageCandidateTitleKey)}
            </p>
            <small
              // eslint-disable-next-line max-len
              className="block text-center text-base font-normal text-[#595959] opacity-75"
            >
              {t(constant.translation.login.pageCandidateSubTitleKey)}
            </small>
          </header>

          <Form
            key="signin-form"
            ref={this.formSignInRef}
            model={this.formLoginSchemaModel}
            onChange={(v: object) => this.onFormValueChanged(v, 'login')}
            formValue={_.get(this.state, 'login')}
            fluid
          >
            <Form.Group className="mt-10">
              <Form.ControlLabel>
                {t(constants.translation.general.formEmailKey)}
              </Form.ControlLabel>
              <Form.Control
                name="email"
                placeholder="Enter your email address"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    this.onSignInButtonClicked();
                  }
                }}
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>
                {t(constants.translation.general.formPasswordKey)}
              </Form.ControlLabel>
              <InputGroup inside>
                <Form.Control
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password here"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      this.onSignInButtonClicked();
                    }
                  }}
                />

                <InputGroup.Button
                  onClick={() => this.setState({ showPassword: !showPassword })}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </InputGroup.Button>
              </InputGroup>
            </Form.Group>

            <div
              // eslint-disable-next-line max-len
              className="mb-8 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 font-normal"
            >
              <span className="flex items-center gap-2">
                <Checkbox
                  onChange={() => this.setState({ rememberMe: !rememberMe })}
                />
                Remember Me
              </span>

              <a
                href="/forgot-password"
                className="cursor-pointer text-[#212121] hover:!text-[#212121]"
              >
                Forgot Password?
              </a>
            </div>

            <Button
              className="btn-tg-primary"
              block
              appearance="primary"
              onClick={this.onSignInButtonClicked}
            >
              {t(constant.translation.general.formLoginButtonKey)}
            </Button>

            <p className="mt-5 text-center font-normal">
              Don&rsquo;t have an account? &nbsp;
              <a
                href="/sign-up"
                // eslint-disable-next-line max-len
                className="cursor-pointer font-bold text-[#004AAD] hover:!text-[#004AAD]"
              >
                Create an Account
              </a>
            </p>

            <p className="my-5 text-center font-normal">or</p>

            <Button
              className="btn-tg-google mt-5"
              block
              appearance="primary"
              onClick={this.onGoogleSignInButtonClicked}
            >
              <img alt="logo" src={googleLogo} />
              <div>
                {t(
                  constant.translation.login.pageCandidateContinueWithLabelKey,
                )}
                <b>Google</b>
              </div>
            </Button>

            <Button
              className="btn-tg-linkedin !mt-5"
              block
              appearance="primary"
              onClick={this.onLinkedInSignInButtonClicked}
            >
              <img alt="logo" src={linkedinLogo} />
              <div>
                {t(
                  constant.translation.login.pageCandidateContinueWithLabelKey,
                )}
                <b>LinkedIn</b>
              </div>
            </Button>
          </Form>
        </div>
      </div>
    );
  }
}

export default withRouter(withTranslation()(CandidateLoginScreen));
